import { FormikErrors } from 'formik';
import {
  areAllDisclaimerItemsConfirmed,
  createInitialDisclaimerConfirmations,
  getPreScanQuestionnaireDisclaimerItems,
} from '../utils/preScanQuestionnaireDisplay';

export interface PreScanQuestionnaireFormData {
  center_address: string;
  postcode: string;
  past_ongoing_medical_conditions: string;
  past_ongoing_medical_conditions_details: string;
  any_surgery_past_3_month: string;
  any_surgery_past_3_month_details: string;
  medications_supplements: string;
  medications_supplements_details: string;
  cancer_history: string;
  cancer_history_details: string;
  any_previous_imaging: string;
  any_previous_imaging_details: string;
  reason_for_scan: string;
  current_symptoms: string;
  metallic_piercing_object_shrapnel: string;
  pacemaker_defibrillator: string;
  metalwork_fractures_surgery: string;
  joint_limb_replacement: string;
  contraceptive_coil: string;
  hearing_devices: string;
  hydrocephalus_shunt: string;
  dental_implants: string;
  metallic_objects_shrapnel_eye: string;
  aneurysm_clips: string;
  metallic_implants_details: string;
  infection_risk: string;
  diabetic: string;
  special_needs: string;
  claustrophobia: string;
  any_allergies: string;
  infection_allergy_details: string;
}

export interface PreScanQuestionnaireFormValues extends PreScanQuestionnaireFormData {
  disclaimer_confirmations: Record<string, boolean>;
}

export const initialPreScanQuestionnaireForm = (): PreScanQuestionnaireFormData => ({
  center_address: '',
  postcode: '',
  past_ongoing_medical_conditions: '',
  past_ongoing_medical_conditions_details: '',
  any_surgery_past_3_month: '',
  any_surgery_past_3_month_details: '',
  medications_supplements: '',
  medications_supplements_details: '',
  cancer_history: '',
  cancer_history_details: '',
  any_previous_imaging: '',
  any_previous_imaging_details: '',
  reason_for_scan: '',
  current_symptoms: '',
  metallic_piercing_object_shrapnel: '',
  pacemaker_defibrillator: '',
  metalwork_fractures_surgery: '',
  joint_limb_replacement: '',
  contraceptive_coil: '',
  hearing_devices: '',
  hydrocephalus_shunt: '',
  dental_implants: '',
  metallic_objects_shrapnel_eye: '',
  aneurysm_clips: '',
  metallic_implants_details: '',
  infection_risk: '',
  diabetic: '',
  special_needs: '',
  claustrophobia: '',
  any_allergies: '',
  infection_allergy_details: '',
});

export const initialPreScanQuestionnaireFormValues = (
  gender?: string | null
): PreScanQuestionnaireFormValues => ({
  ...initialPreScanQuestionnaireForm(),
  disclaimer_confirmations: createInitialDisclaimerConfirmations(gender),
});

const UK_POSTCODE_REGEX = /[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}/i;

export const extractPostcodeFromAddress = (address: string): string => {
  const match = address.match(UK_POSTCODE_REGEX);
  return match ? match[0].trim().toUpperCase() : '';
};

export const REQUIRED_YES_NO_FIELDS: Array<keyof PreScanQuestionnaireFormData> = [
  'past_ongoing_medical_conditions',
  'any_surgery_past_3_month',
  'medications_supplements',
  'cancer_history',
  'any_previous_imaging',
  'metallic_piercing_object_shrapnel',
  'pacemaker_defibrillator',
  'metalwork_fractures_surgery',
  'joint_limb_replacement',
  'contraceptive_coil',
  'hearing_devices',
  'hydrocephalus_shunt',
  'dental_implants',
  'metallic_objects_shrapnel_eye',
  'aneurysm_clips',
  'infection_risk',
  'diabetic',
  'special_needs',
  'claustrophobia',
  'any_allergies',
];

export const YES_NO_DETAIL_FIELD_PAIRS: Partial<
  Record<keyof PreScanQuestionnaireFormData, keyof PreScanQuestionnaireFormData>
> = {
  past_ongoing_medical_conditions: 'past_ongoing_medical_conditions_details',
  any_surgery_past_3_month: 'any_surgery_past_3_month_details',
  medications_supplements: 'medications_supplements_details',
  cancer_history: 'cancer_history_details',
  any_previous_imaging: 'any_previous_imaging_details',
};

export const REQUIRED_TEXT_FIELDS: Array<keyof PreScanQuestionnaireFormData> = [
  'reason_for_scan',
  'current_symptoms',
];

export const MEDICAL_IMPLANT_YES_NO_FIELDS: Array<keyof PreScanQuestionnaireFormData> = [
  'metallic_piercing_object_shrapnel',
  'pacemaker_defibrillator',
  'metalwork_fractures_surgery',
  'joint_limb_replacement',
  'contraceptive_coil',
  'hearing_devices',
  'hydrocephalus_shunt',
  'dental_implants',
  'metallic_objects_shrapnel_eye',
  'aneurysm_clips',
];

export const INFECTION_RISK_YES_NO_FIELDS: Array<keyof PreScanQuestionnaireFormData> = [
  'infection_risk',
  'diabetic',
  'special_needs',
  'claustrophobia',
  'any_allergies',
];

export const VALIDATION_MESSAGES = {
  REQUIRED: 'Required',
  PLEASE_PROVIDE_DETAILS: 'Please provide details',
} as const;

export const hasAnyYesAnswer = (
  values: PreScanQuestionnaireFormData,
  fields: Array<keyof PreScanQuestionnaireFormData>
): boolean => fields.some((field) => values[field] === 'Yes');

export const validatePreScanQuestionnaireForm = (
  values: PreScanQuestionnaireFormValues,
  gender?: string | null
): FormikErrors<PreScanQuestionnaireFormValues> => {
  const errors: FormikErrors<PreScanQuestionnaireFormValues> = {};

  if (!values.center_address.trim()) {
    errors.center_address = VALIDATION_MESSAGES.REQUIRED;
  }

  for (const field of REQUIRED_YES_NO_FIELDS) {
    if (!values[field]) {
      errors[field] = VALIDATION_MESSAGES.REQUIRED;
    }
  }

  for (const field of REQUIRED_TEXT_FIELDS) {
    if (!values[field].trim()) {
      errors[field] = VALIDATION_MESSAGES.REQUIRED;
    }
  }

  for (const [yesNoField, detailsField] of Object.entries(YES_NO_DETAIL_FIELD_PAIRS) as Array<
    [keyof PreScanQuestionnaireFormData, keyof PreScanQuestionnaireFormData]
  >) {
    if (values[yesNoField] === 'Yes' && !values[detailsField].trim()) {
      errors[detailsField] = VALIDATION_MESSAGES.PLEASE_PROVIDE_DETAILS;
    }
  }

  if (
    hasAnyYesAnswer(values, MEDICAL_IMPLANT_YES_NO_FIELDS) &&
    !values.metallic_implants_details.trim()
  ) {
    errors.metallic_implants_details = VALIDATION_MESSAGES.PLEASE_PROVIDE_DETAILS;
  }

  if (
    hasAnyYesAnswer(values, INFECTION_RISK_YES_NO_FIELDS) &&
    !values.infection_allergy_details.trim()
  ) {
    errors.infection_allergy_details = VALIDATION_MESSAGES.PLEASE_PROVIDE_DETAILS;
  }

  const disclaimerItems = getPreScanQuestionnaireDisclaimerItems(gender);
  const disclaimerErrors: Record<string, string> = {};
  for (const item of disclaimerItems) {
    if (!values.disclaimer_confirmations[item]) {
      disclaimerErrors[item] = VALIDATION_MESSAGES.REQUIRED;
    }
  }
  if (Object.keys(disclaimerErrors).length > 0) {
    errors.disclaimer_confirmations = disclaimerErrors;
  }

  return errors;
};

export const mapScanInfoToForm = (
  scanInfo?: Record<string, unknown> | null
): PreScanQuestionnaireFormData => {
  const form = initialPreScanQuestionnaireForm();

  if (!scanInfo) {
    return form;
  }

  (Object.keys(form) as Array<keyof PreScanQuestionnaireFormData>).forEach((field) => {
    const value = scanInfo[field];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      form[field] = String(value);
    }
  });

  if (
    form.center_address &&
    form.postcode &&
    !form.center_address.toUpperCase().includes(form.postcode.toUpperCase())
  ) {
    form.center_address = `${form.center_address}, ${form.postcode}`;
  } else if (!form.center_address && form.postcode) {
    form.center_address = form.postcode;
  }

  return form;
};

export const formToScanInfoPayload = (
  form: PreScanQuestionnaireFormData,
  disclaimerConfirmations: Record<string, boolean>,
  gender?: string | null
): Record<string, string | boolean | Record<string, boolean>> => {
  const centerAddress = form.center_address.trim();
  const extractedPostcode = form.postcode.trim() || extractPostcodeFromAddress(centerAddress);

  return {
    ...form,
    center_address: centerAddress,
    postcode: extractedPostcode,
    disclaimer_confirmations: disclaimerConfirmations,
    pre_scan_questionnaire_confirmed: areAllDisclaimerItemsConfirmed(
      disclaimerConfirmations,
      gender
    ),
  };
};
